import React, {ChangeEvent, useEffect, useRef, useState} from 'react';
import {DayMouseEventHandler, DayPicker, Matcher} from 'react-day-picker';
import dayjs, {Dayjs} from 'dayjs';
import {differenceInDays} from 'date-fns';
import {KeyEventEnum} from './DatePicker';
import {detect} from 'detect-browser';

export interface DateRangeInputNew {
  startDate?: Dayjs;
  endDate?: Dayjs;
}
export interface CalendarDateRangePickerProps {
  isStartDateInput: boolean;
  startDate?: Dayjs;
  endDate?: Dayjs;
  onChange: (dates: DateRangeInputNew) => void;
  isInvalid?: boolean;
  inputStyle?: React.CSSProperties;
  isDateTimeInput?: boolean;
  id?: string;
}

export interface CalendarDateRangePickerState {
    startDateValue?: string;
    endDateValue?: string;
    startDate?: Date;
    endDate?: Date;
    range?: Matcher;
    hoverRange?: Matcher;
    separateDate?: Date[];
    isInvalid: boolean;
    id: string;
    month: Date;
}

export const DATE_FORMAT_RANGE_PICKER = 'DD.MM.YYYY';

const CalendarDateRangePicker: React.FC<CalendarDateRangePickerProps> = (props) => {

  const [dateRangePickerState, setDateRangePickerState] = useState<CalendarDateRangePickerState>({
      startDateValue: props.startDate ? props.startDate.startOf('day').format(DATE_FORMAT_RANGE_PICKER) : undefined,
      endDateValue: props.endDate ? props.endDate.startOf('day').format(DATE_FORMAT_RANGE_PICKER) : undefined,
      range: (props.startDate && props.endDate && dayjs(props.endDate.startOf('day')).isAfter(props.startDate
          .startOf('day'))) ? {from: dayjs(props.startDate).add(1, 'day').toDate(),
          to: dayjs(props.endDate).subtract(1, 'day').toDate()} : undefined,
      startDate: props.startDate ? props.startDate.startOf('day').toDate() : undefined,
      endDate: props.endDate ? props.endDate.startOf('day').toDate() : undefined,
      separateDate: (!props.startDate && props.endDate) ? [props.endDate.toDate()] : (!props.endDate && props.startDate)
          ? [props.startDate.toDate()] : [],
      id: props.id ? props.id : '0',
      isInvalid: false,
      month: (props.isStartDateInput && props.startDate) ? props.startDate.toDate() :
          (!props.isStartDateInput && props.endDate) ? props.endDate.toDate() : new Date()
  });
  const [open, setOpen] = useState(false);
  const [isSettingStartDate, setIsSettingStartDate] = useState(props.isStartDateInput);
  const [hasClickedMonthChange, setHasClickedMonthChange] = useState(false);
  const monthChangeClickedRef = useRef(hasClickedMonthChange);

  useEffect(() => {
    monthChangeClickedRef.current = hasClickedMonthChange;
  }, [hasClickedMonthChange]);

  useEffect(() => {
      setDateRangePickerState({
          range: (props.startDate && props.endDate && dayjs(props.endDate.startOf('day'))
              .isAfter(props.startDate.startOf('day'))) ?
              {from: dayjs(props.startDate).add(1, 'day').toDate(),
              to: dayjs(props.endDate).subtract(1, 'day').toDate()} : undefined,
          separateDate: (!props.startDate && props.endDate) ? [props.endDate.toDate()] : (!props.endDate && props.startDate)
              ? [props.startDate.toDate()] : [],
          id: props.id ? props.id : '0',
          isInvalid: !!(props.isInvalid || (props.startDate && props.endDate &&
              ((!!props.isDateTimeInput && dayjs(props.startDate.startOf('day')).isSame(props.endDate
                  .startOf('day'))) ? false : !dayjs(props.endDate.startOf('day'))
                  .isAfter(props.startDate.startOf('day'))))),
          month: (props.isStartDateInput && props.startDate) ? props.startDate.toDate() :
              (!props.isStartDateInput && props.endDate) ? props.endDate.toDate() : new Date(),
          startDate: props.startDate ? props.startDate.startOf('day').toDate() : undefined,
          endDate: props.endDate ? props.endDate.startOf('day').toDate() : undefined,
          startDateValue: props.startDate ? props.startDate.startOf('day').format(DATE_FORMAT_RANGE_PICKER) :
              undefined,
          endDateValue: props.endDate ? props.endDate.startOf('day').format(DATE_FORMAT_RANGE_PICKER) : undefined,
      });
  }, [props.startDate, props.endDate, props.id, props.isInvalid, props.isStartDateInput, props.isDateTimeInput]);

  const toggle = (openVal: boolean) => {
      if (!open && openVal) {
          setIsSettingStartDate(props.isStartDateInput);
      }
      setDateRangePickerState({...dateRangePickerState, hoverRange: undefined});
      setOpen(openVal);
  };

  const onBlur = (c: string | undefined) => {
      const browser = detect();
      if (browser?.name === 'safari') {
          setTimeout(() => {
              if (monthChangeClickedRef.current) {
                  setHasClickedMonthChange(false);
                  return;
              }
              handleBlurActions(c);
          }, 100);
      } else {
          handleBlurActions(c);
      }
      setHasClickedMonthChange(false);
  };

  const handleBlurActions = (c: string | undefined) => {
      if (!checkClassNameNotDatepicker(c)) {
          return;
      }
      toggle(false);
      handleSetNewDateValue(true);
  };

  const handleDayClick: DayMouseEventHandler = (day, modifiers, e) => {
      setHasClickedMonthChange(false);
      const oldDateRangePickerState = {...dateRangePickerState};
      let selectedStartDate = oldDateRangePickerState.startDate;
      let selectedEndDate = oldDateRangePickerState.endDate;

      if (isSettingStartDate) {
          oldDateRangePickerState.startDate = day;
          oldDateRangePickerState.startDateValue = dayjs(day).format(DATE_FORMAT_RANGE_PICKER);
          oldDateRangePickerState.range = undefined;
          selectedStartDate = day;
      } else {
          oldDateRangePickerState.endDate = day;
          oldDateRangePickerState.endDateValue = dayjs(day).format(DATE_FORMAT_RANGE_PICKER);
          setOpen(false);
          selectedEndDate = day;
      }

      const res = setRangeSeparateDatesAndValidation(oldDateRangePickerState.startDate, oldDateRangePickerState.endDate);
      oldDateRangePickerState.range = res.range;
      oldDateRangePickerState.isInvalid = res.inValid;
      oldDateRangePickerState.separateDate = res.separateDates;
      oldDateRangePickerState.hoverRange = undefined;

      props.onChange({startDate: selectedStartDate ? dayjs(selectedStartDate) : undefined,
          endDate: selectedEndDate ? dayjs(selectedEndDate) : undefined});

      setIsSettingStartDate(!isSettingStartDate);
      setDateRangePickerState({...oldDateRangePickerState});
  };

    const checkClassNameNotDatepicker = (cl: string | undefined): boolean => {
        if (!cl) return true;
        for (const l of ['date-input-box-' + dateRangePickerState.id, 'rdp', 'date-selection-' +
        dateRangePickerState.id]) {
            if (cl.includes(l))
                return false;
        }
        return true;
    };

  const handleEnterPressed = (e: any) => {
    if (e.key === KeyEventEnum.ENTER) {
        handleSetNewDateValue(false);
    }
  };

  const handleDatePickerContainerOnBlur = (targetClassName?: string) => {
    setHasClickedMonthChange(false);
    if (!targetClassName) {
        toggle(false);
        return;
    }
    if (checkClassNameNotDatepicker(targetClassName))
        toggle(false);
  };

  const handleSetNewDateValue = (isBlurEvent: boolean) => {
      setHasClickedMonthChange(false);
      if ((props.isStartDateInput && !dateRangePickerState.startDateValue) ||
          (!props.isStartDateInput && !dateRangePickerState.endDateValue)) {
          setDateRangePickerState({ ...dateRangePickerState, isInvalid: false });
          setOpen(false);
          props.onChange({startDate: dateRangePickerState.startDate ? dayjs(dateRangePickerState.startDate) :
                  undefined, endDate: dateRangePickerState.endDate ? dayjs(dateRangePickerState.endDate) : undefined});
          return;
      }
      const newDateRangePickerState = {...dateRangePickerState};
      if (props.isStartDateInput && dateRangePickerState.startDateValue &&
          dayjs(dateRangePickerState.startDateValue, DATE_FORMAT_RANGE_PICKER).isValid()) {
          newDateRangePickerState.isInvalid = false;
          const newDate = dayjs(dateRangePickerState.startDateValue, DATE_FORMAT_RANGE_PICKER);
          newDateRangePickerState.startDate = newDate.toDate();
      } else if (!props.isStartDateInput && dateRangePickerState.endDateValue &&
          dayjs(dateRangePickerState.endDateValue, DATE_FORMAT_RANGE_PICKER).isValid()) {
          newDateRangePickerState.isInvalid = false;
          const newDate = dayjs(dateRangePickerState.endDateValue, DATE_FORMAT_RANGE_PICKER);
          newDateRangePickerState.endDate = newDate.toDate();
      } else {
          newDateRangePickerState.isInvalid = true;
      }
      props.onChange({startDate: newDateRangePickerState.startDate ? dayjs(newDateRangePickerState.startDate) :
          undefined, endDate: newDateRangePickerState.endDate ? dayjs(newDateRangePickerState.endDate) : undefined});
      if (!isBlurEvent) {
          setOpen(false);
      }
      const res = setRangeSeparateDatesAndValidation(newDateRangePickerState.startDate, newDateRangePickerState.endDate);
      newDateRangePickerState.range = res.range;
      newDateRangePickerState.separateDate = res.separateDates;
      newDateRangePickerState.isInvalid = res.inValid;
      setDateRangePickerState({...newDateRangePickerState});
  };

    const onValueChange = (value: ChangeEvent<HTMLInputElement>) => {
        const dateString = value.target.value;
        const oldPickerState = {...dateRangePickerState};
        const validDate = dayjs(dateString, DATE_FORMAT_RANGE_PICKER).isValid();
        if (props.isStartDateInput) {
            oldPickerState.startDateValue = dateString;
        } else {
            oldPickerState.endDateValue = dateString;
        }
        if (props.isStartDateInput && validDate) {
            const newDate = dayjs(dateString, DATE_FORMAT_RANGE_PICKER);
            oldPickerState.startDate = newDate.toDate();
            oldPickerState.month = newDate.toDate();
        } else if (props.isStartDateInput) {
            oldPickerState.startDate = undefined;
        } else if (!props.isStartDateInput && validDate) {
            const newDate = dayjs(dateString, DATE_FORMAT_RANGE_PICKER);
            oldPickerState.endDate = newDate.toDate();
            oldPickerState.month = newDate.toDate();
        } else {
            oldPickerState.endDate = undefined;
        }
        setOpen(true);
        const res = setRangeSeparateDatesAndValidation(oldPickerState.startDate, oldPickerState.endDate);
        oldPickerState.range = res.range;
        oldPickerState.separateDate = res.separateDates;
        oldPickerState.isInvalid = res.inValid;
        setDateRangePickerState({...oldPickerState});
    };

    const setRangeSeparateDatesAndValidation = (startDate?: Date, endDate?: Date):
        {range: Matcher | undefined, separateDates: Date[], inValid: boolean} => {
        const separateDates = [];
        if (endDate && (!startDate || dayjs(endDate).isBefore(startDate))) {
            separateDates.push(endDate);
        }
        if (startDate && (!endDate || dayjs(startDate).isAfter(endDate))) {
            separateDates.push(startDate);
        }

        let range = undefined;
        if (startDate && endDate && differenceInDays(endDate, startDate) >= 2 && dayjs(endDate).isAfter(startDate)) {
            range = {after: startDate, before: endDate};
        }

        let isInvalid = false;
        if (startDate && endDate && ((props.isDateTimeInput && dayjs(endDate).isSame(startDate)) ? false :
            !dayjs(endDate).isAfter(startDate))) {
            isInvalid = true;
        }

        return {
            range: range,
            separateDates: separateDates,
            inValid: isInvalid
        };
    };

  const getModifier = (m: any): Matcher => {
      if (m) {
          return m as Matcher;
      } else {
          return false;
      }
  };

  const handleMouseHoverDay = (d: Date) => {
      if (!isSettingStartDate && dateRangePickerState.startDate &&
          dayjs(d).isAfter(dateRangePickerState.startDate)) {
          setDateRangePickerState({
              ...dateRangePickerState,
              hoverRange: {
                  from: dayjs(dateRangePickerState.startDate).add(1, 'day').toDate(),
                  to: d
              }
          });
      } else if (isSettingStartDate && dateRangePickerState.endDate &&
          dayjs(d).isBefore(dateRangePickerState.endDate)) {
          setDateRangePickerState({
              ...dateRangePickerState,
              hoverRange: {
                  from: d,
                  to: dayjs(dateRangePickerState.endDate).subtract(1, 'day').toDate()
              }
          });
      }
  };

  const handleMouseHoverDayLeave = () => {
      setDateRangePickerState({...dateRangePickerState, hoverRange: undefined});
  };

  return (
    <div className={'datepicker-container'}>
        <input
            value={(props.isStartDateInput ? dateRangePickerState.startDateValue :
                dateRangePickerState.endDateValue) || ''}
            id={props.id}
            onClick={(e: any) => {
                toggle(true);
                e.target.focus();
            }}
            placeholder={DATE_FORMAT_RANGE_PICKER}
            className={`date-input-box-${dateRangePickerState
                .id} form-control${dateRangePickerState.isInvalid && ' is-invalid' || ''}`}
            onFocus={() => toggle(true)}
            onBlur={(e) => onBlur(e.relatedTarget?.className)}
            onChange={(value) => onValueChange(value)}
            style={props.inputStyle || {}}
            onKeyPress={handleEnterPressed}
            autoComplete='off'
        />
        {open &&
        <div
            onBlur={(e) => handleDatePickerContainerOnBlur(e.relatedTarget?.className)}
            className={'date-selection-container date-selection-' + dateRangePickerState.id}
            tabIndex={0}
        >
            <DayPicker
                onDayClick={(d, m, e) => handleDayClick(d, m, e)}
                defaultMonth={dateRangePickerState.month}
                month={dateRangePickerState.month}
                onMonthChange={(m) => {
                    setHasClickedMonthChange(true);
                    setDateRangePickerState({...dateRangePickerState, month: m});
                }}
                modifiers={{
                    'range-start': getModifier(dateRangePickerState.startDate),
                    'range-end': getModifier(dateRangePickerState.endDate),
                    'range-middle': getModifier(dateRangePickerState.range),
                    'separate-date': getModifier(dateRangePickerState.separateDate),
                    'range-hover': getModifier(dateRangePickerState.hoverRange)
                }}
                onDayMouseEnter={(d) => handleMouseHoverDay(d)}
                onDayMouseLeave={() => handleMouseHoverDayLeave()}
                numberOfMonths={2}
                modifiersClassNames={{'range-start': 'range-start', 'range-end': 'range-end',
                    'range-middle': 'range-middle', 'separate-date': 'separate-date', 'range-hover': 'range-hover' }}
            />
        </div>
        }
    </div>
  );
};

export default CalendarDateRangePicker;
